/**
 * 내 계정 — 비밀번호와 로그인된 기기.
 *
 * 세션 목록을 두는 이유는 하나다. 비밀번호가 샜다고 의심하는 사람이 **스스로**
 * 확인하고 끊을 수 있어야 한다. 관리자에게 요청해야만 가능한 조치라면 대부분
 * 그냥 넘어가고, 그 사이 세션은 살아 있다.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import type { Me, SignageOneClient } from '../api/client.js';
import { Badge, ErrorNote, Loading, Panel, RelTime } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { useAction, useAsync } from '../hooks.js';
import { roleLabel } from '../roles.js';
import { PASSWORD_MIN } from '../password.js';
import { confirmDialog } from '../components/ConfirmDialog.js';

export function AccountScreen({ client, me }: { client: SignageOneClient; me: Me | null }): ReactNode {
  const local = me?.auth === 'local';
  const sessions = useAsync(() => (local ? client.listSessions() : Promise.resolve(null)), [client, local]);
  const action = useAction();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (mismatch) return;
    void action.run(
      () => client.changePassword({ current_password: current, new_password: next }),
      () => {
        setCurrent('');
        setNext('');
        setConfirm('');
        setDone(true);
        sessions.reload();
      },
    );
  };

  return (
    <div className="grid grid-cols-1 gap-5 animate-fade-in xl:grid-cols-2">
      <Panel title="계정">
        <div className="p-4">
          <dl className="kv">
            <dt>이름</dt>
            <dd>{me?.account?.name ?? '—'}</dd>
            <dt>이메일</dt>
            <dd>{me?.account?.email ?? me?.id ?? '—'}</dd>
            <dt>역할</dt>
            <dd>
              <Badge tone="brand">{me ? roleLabel(me.role) : '—'}</Badge>
            </dd>
            <dt>로그인 방식</dt>
            <dd>
              {me?.auth === 'local'
                ? me.account?.auth_source === 'oidc'
                  ? '사내 계정'
                  : '로컬 계정'
                : me?.auth === 'host'
                  ? `연동 서비스 (${me.issuer})`
                  : '서비스 토큰'}
            </dd>
          </dl>
        </div>
      </Panel>

      {/* 사내 계정은 비밀번호가 없다 — 바꾸는 곳은 사내 계정 시스템이다. 세션 목록은 그대로 쓴다. */}
      {local && me?.account?.auth_source !== 'oidc' ? (
        <Panel title="비밀번호 변경">
          <form className="form p-4" onSubmit={submit}>
            <label className="field">
              <span className="field-label">현재 비밀번호</span>
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                required
                value={current}
                disabled={action.pending}
                onChange={(event) => {
                  setCurrent(event.target.value);
                  setDone(false);
                }}
              />
            </label>
            <label className="field">
              <span className="field-label">
                새 비밀번호
                <HelpTip text="짧고 복잡한 문자열보다 긴 문장이 더 안전합니다." label="새 비밀번호 도움말" />
              </span>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                required
                minLength={PASSWORD_MIN}
                value={next}
                disabled={action.pending}
                onChange={(event) => setNext(event.target.value)}
              />
              {/*
                서버가 실제로 막는 것만 적는다(`checkPasswordPolicy`) — 지금은 길이뿐이다.
                막히지 않는 것을 적어 두면 사람은 그것을 스스로 피하고, 그 사이 실제 규칙은
                흐려진다. 예전에 이메일·이름 포함을 안내했던 것이 그랬다.
              */}
              <small className="hint">{PASSWORD_MIN}자 이상</small>
            </label>
            <label className="field">
              <span className="field-label">새 비밀번호 확인</span>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                disabled={action.pending}
                onChange={(event) => setConfirm(event.target.value)}
              />
              {mismatch ? <p className="field-error">비밀번호가 서로 다릅니다</p> : null}
            </label>

            {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
            {done ? (
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13.5px] text-emerald-700">
                비밀번호를 변경했습니다. 다른 기기의 로그인은 모두 해제되었습니다.
              </p>
            ) : null}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={action.pending}>
                {action.pending ? '변경 중…' : '비밀번호 변경'}
              </button>
            </div>
          </form>
        </Panel>
      ) : (
        <Panel title="비밀번호">
          <p className="hint p-4">
            {me?.account?.auth_source === 'oidc'
              ? '사내 계정으로 로그인했습니다. 비밀번호·2단계 인증은 사내 계정 시스템에서 관리합니다.'
              : '연동 서비스 계정으로 로그인했습니다. 비밀번호는 연동 서비스에서 관리합니다.'}
          </p>
        </Panel>
      )}

      {local ? (
        <div className="xl:col-span-2">
          <Panel
            title="로그인된 기기"
            help="모르는 기기가 있으면 비밀번호를 변경하세요. 변경하면 다른 기기의 로그인이 모두 해제됩니다."
            actions={
              <button
                type="button"
                className="btn"
                disabled={action.pending}
                onClick={async () => {
                  const ok = await confirmDialog({
                    title: '다른 기기의 로그인을 모두 해제할까요?',
                    body: '지금 쓰는 이 기기는 그대로 로그인되어 있습니다.',
                    confirmLabel: '모두 로그아웃',
                    danger: true,
                  });
                  if (!ok) return;
                  void action.run(() => client.revokeOtherSessions(), sessions.reload, '다른 기기의 로그인을 모두 해제했습니다');
                }}
              >
                다른 기기 모두 로그아웃
              </button>
            }
          >
            {sessions.loading && !sessions.data ? <Loading /> : null}
            {sessions.error ? <ErrorNote message={sessions.error} /> : null}

            {sessions.data ? (
              <table className="table min-w-[32rem]">
                <thead>
                  <tr>
                    <th>기기</th>
                    <th>IP</th>
                    <th>마지막 사용</th>
                    <th>로그인 시각</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.data.items.map((session) => (
                    <tr key={session.id}>
                      <td className="cell-strong">
                        {describeAgent(session.user_agent)}
                        {session.current ? <Badge tone="ok">이 기기</Badge> : null}
                      </td>
                      <td className="mono">{session.ip || '—'}</td>
                      <td className="cell-muted"><RelTime iso={session.last_seen_at} /></td>
                      <td className="cell-muted"><RelTime iso={session.created_at} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </Panel>
        </div>
      ) : null}
    </div>
  );
}

/**
 * User-Agent 를 사람이 알아볼 한 줄로.
 *
 * 완벽한 파싱을 노리지 않는다 — "내가 쓰는 기기인가"를 판단할 정도면 충분하고,
 * 그 이상은 UA 문자열로 알 수 없다. 못 알아보면 원문 앞부분을 그대로 보여준다.
 */
export function describeAgent(userAgent: string): string {
  if (!userAgent.trim()) return '알 수 없는 기기';

  const browser =
    /Edg\//.test(userAgent) ? 'Edge'
    : /OPR\//.test(userAgent) ? 'Opera'
    : /Chrome\//.test(userAgent) ? 'Chrome'
    : /Safari\//.test(userAgent) ? 'Safari'
    : /Firefox\//.test(userAgent) ? 'Firefox'
    : null;

  const platform =
    /iPhone|iPad/.test(userAgent) ? 'iOS'
    : /Android/.test(userAgent) ? 'Android'
    : /Mac OS X/.test(userAgent) ? 'macOS'
    : /Windows/.test(userAgent) ? 'Windows'
    : /Linux/.test(userAgent) ? 'Linux'
    : null;

  if (browser && platform) return `${browser} · ${platform}`;
  if (browser) return browser;
  if (platform) return platform;
  return userAgent.slice(0, 40);
}
